/**
 * Change the same field on many moulds at once.
 *
 * The rule the whole dialog is built around: **a field you do not touch is not
 * written.** Every control starts on "Leave unchanged", and only the ones moved
 * off it end up in the patch. A bulk editor that submits its whole form is how
 * twenty moulds silently lose their designer because the form defaulted to
 * blank.
 */
import { useMemo, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import { Dialog } from '../../components/ui/dialog.js';
import { Button } from '../../components/ui/button.js';
import { Field, Input, Select } from '../../components/ui/field.js';
import { Code } from '../../components/ui/primitives.js';
import { updateRow } from '../../db/mutations.js';
import { useMasterOptions, type CatalogMasterTable, type MouldRow } from '../../db/queries.js';

/** Only fields where "set them all to the same value" is a sane thing to want. */
const FIELDS: Array<{ name: string; label: string; master?: CatalogMasterTable; kind?: 'number' }> = [
  { name: 'projectId', label: 'Project', master: 'projects' },
  { name: 'ownerCompanyId', label: 'Mould owner', master: 'companies' },
  { name: 'customerCompanyId', label: 'Customer', master: 'companies' },
  { name: 'manufactureCompanyId', label: 'Manufacturing company', master: 'companies' },
  { name: 'designerId', label: 'Designer', master: 'designers' },
  { name: 'toolTypeId', label: 'Tool type', master: 'toolTypes' },
  { name: 'runnerTypeId', label: 'Runner type', master: 'runnerTypes' },
  { name: 'gateTypeId', label: 'Gate type', master: 'gateTypes' },
  { name: 'ejectionTypeId', label: 'Ejection type', master: 'ejectionTypes' },
  { name: 'coreCavityMaterialId', label: 'Core / cavity material', master: 'materials' },
  { name: 'moldBaseMaterialId', label: 'Mould base material', master: 'materials' },
  { name: 'materialGradeId', label: 'Material / shrinkage', master: 'materialGrades' },
  { name: 'mechanismTypeId', label: 'Sliders / lifters', master: 'mechanismTypes' },
  { name: 'manufactureYear', label: 'Manufacturing year', kind: 'number' },
];

/** The sentinel for "don't touch this". Not '' - that is a legitimate "clear it". */
const UNCHANGED = '\u0000unchanged';
const CLEAR = '\u0000clear';

export function MouldBulkEditDialog({
  rows,
  open,
  onOpenChange,
  onDone,
}: {
  rows: MouldRow[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const touched = useMemo(
    () => Object.entries(values).filter(([, v]) => v !== UNCHANGED && v !== ''),
    [values],
  );

  async function apply() {
    if (touched.length === 0) return;
    const patch: Record<string, unknown> = {};
    for (const [name, raw] of touched) {
      const field = FIELDS.find((f) => f.name === name);
      if (raw === CLEAR) patch[name] = null;
      else if (field?.kind === 'number') patch[name] = Number(raw);
      else patch[name] = raw;
    }

    setSaving(true);
    setError(null);
    try {
      // One mutation per row rather than a bulk endpoint: each becomes its own
      // outbox entry, so a rejection on one mould does not take the other
      // nineteen down with it.
      for (const row of rows) await updateRow('moulds', row.id, patch);
      onDone();
      onOpenChange(false);
      setValues({});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not apply the changes.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="md"
      title={`Edit ${rows.length} mould${rows.length === 1 ? '' : 's'}`}
      description="Anything left on “Leave unchanged” is not written."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void apply()}
            loading={saving}
            disabled={touched.length === 0}
          >
            {touched.length === 0
              ? 'Nothing to apply'
              : `Apply ${touched.length} change${touched.length === 1 ? '' : 's'}`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4 pb-2">
        <div className="max-h-24 overflow-y-auto rounded-lg border border-border bg-surface-sunken/40 p-2">
          <div className="flex flex-wrap gap-1.5">
            {rows.map((r) => (
              <Code key={r.id} className="text-xs">
                {r.assetCode}
              </Code>
            ))}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {FIELDS.map((f) => (
            <BulkField
              key={f.name}
              field={f}
              value={values[f.name] ?? UNCHANGED}
              onChange={(v) => setValues((prev) => ({ ...prev, [f.name]: v }))}
            />
          ))}
        </div>

        {touched.length > 0 ? (
          <p className="flex items-start gap-2 rounded-lg bg-warning-subtle px-3 py-2 text-sm text-warning-foreground">
            <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>
              This overwrites {touched.length === 1 ? 'that field' : 'those fields'} on all{' '}
              {rows.length} moulds, including any that already had a different value.
            </span>
          </p>
        ) : null}

        {error ? (
          <p role="alert" className="rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

function BulkField({
  field,
  value,
  onChange,
}: {
  field: (typeof FIELDS)[number];
  value: string;
  onChange: (value: string) => void;
}) {
  const options = useMasterOptions((field.master ?? 'companies') as CatalogMasterTable);

  if (field.kind === 'number') {
    return (
      <Field label={field.label}>
        <div className="flex gap-1.5">
          <Input
            type="number"
            value={value === UNCHANGED || value === CLEAR ? '' : value}
            placeholder="Leave unchanged"
            onChange={(e) => onChange(e.target.value || UNCHANGED)}
          />
        </div>
      </Field>
    );
  }

  return (
    <Field label={field.label}>
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        options={[
          { value: UNCHANGED, label: 'Leave unchanged' },
          { value: CLEAR, label: '- Clear the value -' },
          ...(options ?? []),
        ]}
      />
    </Field>
  );
}
